import React, { useRef, useEffect } from 'react';
import { ArrowDown, ArrowUp, FileText, X } from 'lucide-react';
import { ReportBlock } from '../../../types/report-builder';
import BlockRenderer from './blocks/BlockRenderer';

interface ReportCanvasProps {
  blocks: ReportBlock[];
  isGenerating: boolean;
  onRemoveBlock?: (blockId: string) => void;
  onMoveBlock?: (blockId: string, direction: 'up' | 'down') => void;
  embedded?: boolean;
}

const ReportCanvas: React.FC<ReportCanvasProps> = ({
  blocks,
  isGenerating,
  onRemoveBlock,
  onMoveBlock,
  embedded = false,
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const suppressScrollRef = useRef(false);

  useEffect(() => {
    if (suppressScrollRef.current) {
      suppressScrollRef.current = false;
      return;
    }
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [blocks.length]);

  return (
    <div
      className={
        embedded
          ? 'flex min-h-0 flex-1 flex-col overflow-hidden bg-card print:rounded-none print:border-0 print:bg-white print:shadow-none'
          : 'flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-border/70 bg-card/95 shadow-[var(--shadow-card)] print:rounded-none print:border-0 print:bg-white print:shadow-none'
      }
    >
      <div
        className={
          embedded
            ? 'flex items-center justify-between gap-4 border-b border-border/70 bg-card px-5 py-3 print:hidden'
            : 'flex items-center justify-between gap-4 border-b border-border/70 bg-background/80 px-5 py-3 print:hidden'
        }
      >
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Canvas
          </span>
          <span className="text-[12px] text-muted-foreground">{blocks.length} blocks</span>
        </div>
        {isGenerating && (
          <span className="text-[11px] font-medium text-primary">Building</span>
        )}
      </div>

      <div
        className={
          embedded
            ? 'flex-1 overflow-y-auto bg-card print:overflow-visible print:bg-white'
            : 'flex-1 overflow-y-auto bg-[linear-gradient(180deg,rgba(251,249,246,0.88),rgba(255,255,255,0.96))] print:overflow-visible print:bg-white'
        }
      >
        <div className="px-4 py-4 md:px-6 md:py-6 print:px-8 print:py-8">
          {blocks.length === 0 && !isGenerating ? (
            <div className="flex min-h-[46vh] items-center justify-center print:min-h-[60vh]">
              <div className="w-full max-w-xl rounded-2xl border border-dashed border-primary/20 bg-[var(--go-good-vibes-tint)]/35 px-6 py-10 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <FileText className="h-6 w-6" />
                </div>
                <h3 className="text-[15px] font-semibold text-foreground">Analyst canvas</h3>
                <p className="mt-2 text-[13px] leading-[1.7] text-muted-foreground">
                  Start in chat. Generated sections appear here as the report is built.
                </p>
              </div>
            </div>
          ) : null}

        {blocks.map((block, index) => (
          <div
            key={block.id}
            className="group relative animate-in fade-in slide-in-from-bottom-2 duration-300 print:mb-6 print:break-inside-avoid print:animate-none"
          >
            {/* Block toolbar — visible on hover */}
            <div className="absolute right-2 top-1 z-10 flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 print:hidden">
              <button
                onClick={() => { suppressScrollRef.current = true; onMoveBlock?.(block.id, 'up'); }}
                disabled={index === 0}
                className="rounded-lg border border-border/70 bg-background/85 p-1.5 text-muted-foreground shadow-sm transition hover:border-primary/15 hover:bg-accent hover:text-primary disabled:cursor-not-allowed disabled:opacity-30"
                title="Move up"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => { suppressScrollRef.current = true; onMoveBlock?.(block.id, 'down'); }}
                disabled={index === blocks.length - 1}
                className="rounded-lg border border-border/70 bg-background/85 p-1.5 text-muted-foreground shadow-sm transition hover:border-primary/15 hover:bg-accent hover:text-primary disabled:cursor-not-allowed disabled:opacity-30"
                title="Move down"
              >
                <ArrowDown className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => { suppressScrollRef.current = true; onRemoveBlock?.(block.id); }}
                className="rounded-lg border border-border/70 bg-background/85 p-1.5 text-muted-foreground shadow-sm transition hover:border-destructive/20 hover:bg-destructive/5 hover:text-destructive"
                title="Remove block"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            <BlockRenderer block={block} />
          </div>
        ))}
          {isGenerating && (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground print:hidden">
              <div className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              <span>Building report...</span>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
      </div>
    </div>
  );
};

export default ReportCanvas;
